import _ from 'lodash';

import isGauge from 'types/visTypeCheckers/isGauge';
import isPaceMatrix from 'types/visTypeCheckers/isPaceMatrix';
import isRankingMatrix from 'types/visTypeCheckers/isRankingMatrix';
import isSingleMetricDateMatrix from 'types/visTypeCheckers/isSingleMetricDateMatrix';
import isV5ChartDef from 'types/visTypeCheckers/isV5ChartDef';

import { getBlockComparison } from '../blockComparison';
import {
  getLegacyMatrixChanges,
  guessLegacyMatrixCard,
  isLegacyMatrixComparison,
} from '../buildLegacyMatrixDefinition';
import {
  guessLegacyGadgetCard,
  isLegacyGadget,
  reproducesLegacyGadget,
} from '../legacyGadgetBlocks';
import {
  buildGadgetChartDefinition,
  guessSidecarCard,
  toGridShown,
} from './chartMatching';
import { toComparable, withoutInertValues } from './legacyDefaults';
import { guessRankingTableCard } from './rankingMatrix';
import { StoredGadget } from './types';

// The comparison a block saves on its gadget: a Table's, or a Number's or
// Line's that it shows (see getBlockComparison)
export const getCardComparison = (
  card: Studio.GadgetSidecarCard,
): PersistedComparisonType | undefined => getBlockComparison(card);

// Whether a smart card shows its value alone: its series' comparison is
// 'none', which SmartCardView draws no change for (smart cards are never
// compact). Older gadgets have it misspelled, which useSmartCardData reads.
const isShowingNoChange = (chartDef: V5ChartDefinition): boolean => {
  const [series] = chartDef.series as (V5ChartDefinitionSeries & {
    smartCardComparision?: string;
  })[];
  return (
    !!series &&
    (series.smartCardComparison ?? series.smartCardComparision) === 'none'
  );
};

// Gadget types that draw a comparison. Others (bars, pies, split lines...)
// store one without showing it, so it only costs an extra query, as does a
// smart card showing no change.
const drawsComparison = (chartDef: V5ChartDefinition): boolean =>
  (chartDef.gadgetType === 'smartCard' && !isShowingNoChange(chartDef)) ||
  chartDef.gadgetType === 'matrix' ||
  (chartDef.gadgetType === 'timeSeries' && !chartDef.dimensionB);

// The gadget's comparison, if it shows one. Legacy chart definitions are
// taken at their word. Comparing by day is the same as by the period's
// length.
const getShownComparison = (
  stored: StoredGadget,
): PersistedComparisonType | undefined => {
  const { chartDef, comparison } = stored.gadget;
  if (!comparison) {
    return undefined;
  }
  // A Gauge compares its entity with its peers, not with a period, and a
  // Ranking Matrix, Reminders, Entity Information Table or Group Vs
  // Remainder doesn't compare
  if (
    (isV5ChartDef(chartDef) && !drawsComparison(chartDef)) ||
    isGauge(chartDef) ||
    isRankingMatrix(chartDef) ||
    isLegacyGadget(chartDef)
  ) {
    return undefined;
  }
  return comparison.compareByInterval === 'day'
    ? _.omit(comparison, 'compareByInterval')
    : comparison;
};

// The gadget's chart as it's drawn. A smart card is drawn as a card, which is
// what a Number block without a comparison saves as, when it shows no change:
// on a dashboard without a comparison (useSmartCardData falls back to one),
// or anywhere with its change set to 'none'. On a report it otherwise shows
// the report's comparison. A dashboard matrix without a comparison of its
// own (DashboardGadgetDateScopeProvider gives it no other) shows no change
// cells, whatever its metrics' cell types.
export const getDrawnChartDef = (
  stored: StoredGadget,
): StoredGadget['gadget']['chartDef'] => {
  const { chartDef } = stored.gadget;
  if (!isV5ChartDef(chartDef)) {
    return chartDef;
  }
  if (
    chartDef.gadgetType === 'smartCard' &&
    ((stored.kind !== 'report' && !getShownComparison(stored)) ||
      isShowingNoChange(chartDef))
  ) {
    return { ...chartDef, gadgetType: 'card' };
  }
  if (
    chartDef.gadgetType === 'matrix' &&
    stored.kind === 'dashboard' &&
    !getShownComparison(stored)
  ) {
    return {
      ...chartDef,
      series: chartDef.series.map(
        ({ matrixCellType, matrixShowComparisonValue, ...s }) => s,
      ),
    };
  }
  return chartDef;
};

// Whether Studio can show the gadget's comparison: the previous period
// (by the period's length or, on a Number or Line, a whole interval) or last
// year, or a date comparison or pace table's window. One the chart doesn't
// show counts as none; it's dropped when the block is saved.
const isStudioComparison = (stored: StoredGadget): boolean => {
  const comparison = getShownComparison(stored);
  const { chartDef } = stored.gadget;
  if (!comparison) {
    return true;
  }
  if (isSingleMetricDateMatrix(chartDef) || isPaceMatrix(chartDef)) {
    return isLegacyMatrixComparison({ chartDef, comparison });
  }
  if (!isV5ChartDef(chartDef)) {
    return false;
  }
  const keys = Object.keys(comparison);
  if (comparison.compareByInterval) {
    return (
      comparison.compareType === 'previous' &&
      _.isEqual(_.sortBy(keys), ['compareByInterval', 'compareType']) &&
      (chartDef.gadgetType === 'smartCard' ||
        chartDef.gadgetType === 'timeSeries')
    );
  }
  return (
    _.isEqual(keys, ['compareType']) &&
    (comparison.compareType === 'previous' ||
      comparison.compareType === 'lastYear')
  );
};

// Whether the block's settings give the gadget's comparison
export const hasSameComparison = ({
  card,
  stored,
}: {
  card: Studio.GadgetSidecarCard;
  stored: StoredGadget;
}): boolean => _.isEqual(getCardComparison(card), getShownComparison(stored));

// Gadget settings Studio can't show or edit yet. A gadget using any of them
// opens as a Classic block. The rest (bonus period, week start, entity and
// default filters) are block settings (readGadgetSettings).
export const getUnsupportedGadgetSettings = (stored: StoredGadget): string[] =>
  isStudioComparison(stored) ? [] : ['Comparison'];

const CHART_SETTING_LABELS: Partial<Record<keyof V5ChartDefinition, string>> = {
  series: 'Series settings',
  dimensionA: 'Dimension',
  dimensionB: 'Second dimension',
  secondaryDimensionA: 'Secondary row dimension',
  isSecondaryRowsStacked: 'Stacked secondary rows',
  groupByLimit: 'Group limit',
  groupByLimitMode: 'Group limit mode',
  groupBySortBy: 'Group sort',
  grouping: 'Sorting',
  trendByCalendarInterval: 'Interval',
  trendByFiscalInterval: 'Fiscal interval',
  trendByFixedIntervalDays: 'Fixed-day interval',
  trendByHourInterval: 'Hourly interval',
  excludePartialIntervals: 'Excluding partial intervals',
  groupByDayOfWeek: 'Group by day of week',
  groupByMonth: 'Group by month',
  stackedBarOptions: 'Stacked bar options',
  stackedAreaOptions: 'Stacked area options',
  simpleGridConfig: 'Grid settings',
  showDataLabels: 'Data labels',
  labelStep: 'Label step',
  bulletDisplay: 'Bullet display',
  gridSizing: 'Column sizing',
  reverseAxes: 'Reversed axes',
  totalsColumnPosition: 'Totals column',
};

// Without ids, and without the classic editor's ways of saving what Studio
// saves differently (see toComparable), or for a grid, only what it shows
const toComparedSettings = (def: V5ChartDefinition) => {
  const { id, series, ...rest } =
    def.gadgetType === 'simpleGrid' ? toGridShown(def) : toComparable(def);
  return { ...rest, series: series.map(({ id: seriesId, ...s }) => s) };
};

export type Conversion =
  | {
      isConvertible: true;
      card: Studio.GadgetSidecarCard;
      // Labels of the settings converting would lose (empty if none)
      dropped: string[];
    }
  | { isConvertible: false; reason: string };

// What turning a Classic gadget into a Studio block would give, and lose.
// Nothing is changed until the block is saved from Studio.
export const getConversion = (stored: StoredGadget): Conversion => {
  const { chartDef } = stored.gadget;
  // Some accounts have gadgets saved without one, which draw nothing
  if (!chartDef) {
    return { isConvertible: false, reason: 'No chart definition' };
  }
  const drawn = getDrawnChartDef(stored);
  const rankingCard = isRankingMatrix(chartDef)
    ? guessRankingTableCard(chartDef)
    : undefined;
  if (rankingCard) {
    return {
      isConvertible: true,
      card: rankingCard,
      dropped: getUnsupportedGadgetSettings(stored),
    };
  }
  // Date comparison and pace Tables
  if (isSingleMetricDateMatrix(chartDef) || isPaceMatrix(chartDef)) {
    const isReportGadget = stored.kind === 'report';
    const { comparison } = stored.gadget;
    const card = guessLegacyMatrixCard({
      chartDef,
      comparison,
      isReportGadget,
    });
    return {
      isConvertible: true,
      card,
      dropped: _.uniq([
        ...getUnsupportedGadgetSettings(stored),
        ...getLegacyMatrixChanges({
          card,
          chartDef,
          comparison,
          isReportGadget,
        }),
      ]),
    };
  }
  if (isLegacyGadget(chartDef)) {
    const card = guessLegacyGadgetCard(chartDef);
    return {
      isConvertible: true,
      card,
      dropped: [
        ...getUnsupportedGadgetSettings(stored),
        ...(reproducesLegacyGadget({
          card,
          chartDef,
          isReportGadget: stored.kind === 'report',
        })
          ? []
          : ['Chart settings']),
      ],
    };
  }
  if (!isV5ChartDef(drawn)) {
    const legacyType = (chartDef as { type?: string }).type;
    return {
      isConvertible: false,
      reason: `Legacy chart: ${legacyType ?? 'unknown'}`,
    };
  }
  const card = guessSidecarCard({
    chartDef: drawn,
    comparison: stored.gadget.comparison,
    isReportGadget: stored.kind === 'report',
  });
  if (!card) {
    return {
      isConvertible: false,
      reason: `Chart type: ${drawn.gadgetType}`,
    };
  }
  const built = buildGadgetChartDefinition({
    card,
    id: drawn.id,
    name: drawn.name,
  });
  if ('missing' in built) {
    return { isConvertible: false, reason: built.missing };
  }
  const before = withoutInertValues(toComparedSettings(drawn)) as Record<
    string,
    unknown
  >;
  const after = withoutInertValues(
    toComparedSettings(built.chartDefinition),
  ) as Record<string, unknown>;
  const changedChartSettings = _.uniq([
    ...Object.keys(before),
    ...Object.keys(after),
  ])
    .filter((key) => !_.isEqual(before[key], after[key]))
    .map(
      (key) =>
        CHART_SETTING_LABELS[key as keyof V5ChartDefinition] ??
        _.startCase(key),
    );
  const unsupported = getUnsupportedGadgetSettings(stored);
  return {
    isConvertible: true,
    card,
    dropped: [
      ...unsupported,
      // A comparison Studio could show, but the guessed settings don't
      ...(!unsupported.includes('Comparison') &&
      !hasSameComparison({ card, stored })
        ? ['Comparison']
        : []),
      ...changedChartSettings,
    ],
  };
};

// Why a gadget opens as a Classic block rather than a Studio one
export const getClassicReason = (stored: StoredGadget): string => {
  const unsupported = getUnsupportedGadgetSettings(stored);
  if (unsupported.length > 0) {
    return `Uses: ${unsupported.join(', ')}`;
  }
  const conversion = getConversion(stored);
  if (!conversion.isConvertible) {
    return conversion.reason;
  }
  return conversion.dropped.length > 0
    ? `Chart settings: ${conversion.dropped.join(', ')}`
    : 'Chart settings Studio saves differently';
};
